import { BackgroundJobKind } from '@models/backgroundJobs/IBackgroundJob';
import { backgroundJobKind } from './backgroundJobKinds';
import './flyToBell.scss';

/** Что прилетело в колокольчик: подсказка у него говорит, где ждать задачу. */
export interface IBellArrival {
    kind: BackgroundJobKind | null;
    /** Название задачи. */
    title: string;
    /** Подсказка у колокольчика: где следить за задачей. */
    hint: string;
}

/** Цель полёта — колокольчик шапки; он же откликается на прилёт. */
export interface IBellTarget {
    element: HTMLElement;
    onArrive: (arrival: IBellArrival) => void;
}

/** Откуда летит задача: окно или кнопка-источник (элемент — его место в момент полёта) либо снятая рамка. */
export type BellFlightOrigin = Element | DOMRectReadOnly | null | undefined;

export interface IFlyToBellOptions {
    /** Нет — из середины нижнего края экрана. */
    from?: BellFlightOrigin;
    kind?: BackgroundJobKind | null;
    title?: string | null;
    /** Подсказка вместо подсказки вида. */
    hint?: string;
}

export interface IBellFlightRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

export interface IBellFlightFrame extends IBellFlightRect {
    offset: number;
    radius: number;
    opacity: number;
}

export const BELL_FLIGHT_MS = 480;
/** Капсула задачи в полёте. */
export const BELL_FLIGHT_CAPSULE = 40;
/** Капсула тает в колокольчике, уменьшаясь до этого размера. */
const LANDING_SIZE = 26;
/** Доля времени, за которую рамка окна стягивается в капсулу. */
const SHRINK_END = 0.32;
/** С какой доли времени капсула, уже над колокольчиком, тает в нём. */
const FADE_FROM = 0.72;
/** Колокольчик откликается, когда капсула тает в нём, — не ждёт, пока она исчезнет совсем. */
const ARRIVE_AT = 0.78;
/** Насколько дуга отходит от прямой: доля расстояния между началом и концом пути. */
const ARC_BEND = 0.26;
const FRAME_RADIUS = 16;
const SAMPLES = 24;
/** Темп движения по дуге — кривая MORPH_EASE (`useMorphDialog`): cubic-bezier(0.32, 0.72, 0.28, 1). */
export const BELL_FLIGHT_CURVE = [0.32, 0.72, 0.28, 1] as const;
/** Без источника капсула вылетает чуть выше нижнего края экрана. */
const FALLBACK_BOTTOM_GAP = 72;

let target: IBellTarget | null = null;

/** Колокольчик становится целью полётов; вызов-результат снимает его. */
export const registerBellTarget = (next: IBellTarget): (() => void) => {
    target = next;
    return () => {
        if (target === next) target = null;
    };
};

const lerp = (from: number, to: number, t: number) => from + (to - from) * t;
const easeOutQuad = (t: number) => 1 - (1 - t) * (1 - t);

const bezierAxis = (t: number, p1: number, p2: number) =>
    3 * (1 - t) * (1 - t) * t * p1 + 3 * (1 - t) * t * t * p2 + t ** 3;

/** Значение кривой `cubic-bezier` в момент `x`: параметр кривой ищется делением пополам (x(t) монотонна). */
export const cubicBezierAt = ([x1, y1, x2, y2]: readonly [number, number, number, number], x: number): number => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let low = 0;
    let high = 1;
    for (let step = 0; step < 30; step++) {
        const mid = (low + high) / 2;
        if (bezierAxis(mid, x1, x2) < x) low = mid;
        else high = mid;
    }
    return bezierAxis((low + high) / 2, y1, y2);
};

const center = (rect: IBellFlightRect) => ({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });

/**
 * Кадры полёта, равномерные по времени: рамка источника стягивается в капсулу и по дуге (квадратичная кривая, изгиб
 * вверх) летит к колокольчику с темпом MORPH_EASE — быстро уходит и плавно садится, — где уменьшается и тает. Темп
 * зашит в кадры, а не в кривую всей анимации: так стягивание и таяние идут по времени, а не по пути.
 */
export const bellFlightFrames = (
    from: IBellFlightRect,
    to: IBellFlightRect,
    samples: number = SAMPLES,
): IBellFlightFrame[] => {
    const start = center(from);
    const end = center(to);
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const distance = Math.hypot(dx, dy) || 1;
    // из двух нормалей к прямой — та, что смотрит вверх: дуга выгибается над путём
    const normal = dx >= 0 ? { x: dy / distance, y: -dx / distance } : { x: -dy / distance, y: dx / distance };
    const bend = ARC_BEND * distance;
    const control = { x: (start.x + end.x) / 2 + normal.x * bend, y: (start.y + end.y) / 2 + normal.y * bend };

    return Array.from({ length: samples + 1 }, (_, index) => {
        const t = index / samples;
        const path = cubicBezierAt(BELL_FLIGHT_CURVE, t);
        const u = 1 - path;
        const x = u * u * start.x + 2 * u * path * control.x + path * path * end.x;
        const y = u * u * start.y + 2 * u * path * control.y + path * path * end.y;
        const shrink = easeOutQuad(Math.min(1, t / SHRINK_END));
        const landing = Math.max(0, (t - FADE_FROM) / (1 - FADE_FROM));
        const capsule = lerp(BELL_FLIGHT_CAPSULE, LANDING_SIZE, landing);
        const width = lerp(from.width, capsule, shrink);
        const height = lerp(from.height, capsule, shrink);
        return {
            offset: t,
            left: x - width / 2,
            top: y - height / 2,
            width,
            height,
            radius: lerp(FRAME_RADIUS, capsule / 2, shrink),
            opacity: 1 - landing,
        };
    });
};

const visibleRect = (element: Element): IBellFlightRect | null => {
    if (!element.isConnected) return null;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 ? rect : null;
};

const fallbackOrigin = (): IBellFlightRect => ({
    left: window.innerWidth / 2 - BELL_FLIGHT_CAPSULE / 2,
    top: window.innerHeight - FALLBACK_BOTTOM_GAP - BELL_FLIGHT_CAPSULE,
    width: BELL_FLIGHT_CAPSULE,
    height: BELL_FLIGHT_CAPSULE,
});

const originRect = (from: BellFlightOrigin): IBellFlightRect => {
    if (!from) return fallbackOrigin();
    if (from instanceof Element) return visibleRect(from) ?? fallbackOrigin();
    return from.width > 0 && from.height > 0 ? from : fallbackOrigin();
};

/** Рамка элемента сейчас — пока он на экране: окно закроется раньше, чем начнётся полёт. */
export const snapshotBellFlightOrigin = (element: Element | null | undefined): DOMRectReadOnly | null => {
    const rect = element ? visibleRect(element) : null;
    return rect ? DOMRectReadOnly.fromRect(rect) : null;
};

const prefersReducedMotion = (): boolean => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/** В скрытой вкладке анимации стоят, а таймеры замедлены: полёт некому смотреть, прилёт ждал бы секунды. */
const pageHidden = (): boolean => document.visibilityState === 'hidden';

const SVG_NS = 'http://www.w3.org/2000/svg';

const svgNode = (tag: string, attributes: Record<string, string>): SVGElement => {
    const node = document.createElementNS(SVG_NS, tag);
    Object.entries(attributes).forEach(([name, value]) => node.setAttribute(name, value));
    return node;
};

/** Призрак полёта: капсула со значком вида в кольце, как строка раздела «Фоновые задачи». */
export const createBellFlightGhost = (kind: BackgroundJobKind | null): HTMLElement => {
    const ghost = document.createElement('div');
    ghost.className = 'bell-flight';
    ghost.setAttribute('aria-hidden', 'true');
    const mark = svgNode('svg', { class: 'bell-flight__mark', width: '28', height: '28', viewBox: '0 0 28 28' });
    mark.append(
        svgNode('circle', { class: 'bell-flight__track', cx: '14', cy: '14', r: '11' }),
        svgNode('circle', { class: 'bell-flight__arc', cx: '14', cy: '14', r: '11' }),
        svgNode('path', { class: 'bell-flight__icon', d: backgroundJobKind(kind).iconPath }),
    );
    ghost.append(mark);
    return ghost;
};

const toKeyframe = (frame: IBellFlightFrame): Keyframe => ({
    offset: frame.offset,
    transform: `translate(${frame.left}px, ${frame.top}px)`,
    width: `${frame.width}px`,
    height: `${frame.height}px`,
    borderRadius: `${frame.radius}px`,
    opacity: frame.opacity,
});

/**
 * Задача ушла в фон: призрак окна или кнопки-источника улетает в колокольчик, колокольчик откликается и, если нужно,
 * показывает подсказку. Без движения (`prefers-reduced-motion`) и в скрытой вкладке — сразу отклик. `false` — колокольчика нет (страница
 * без шапки): показать, где ждать задачу, вызывающий должен сам.
 */
export const flyToBell = ({ from, kind = null, title, hint }: IFlyToBellOptions = {}): boolean => {
    const landing = target;
    if (!landing) return false;
    const view = backgroundJobKind(kind);
    const arrival: IBellArrival = { kind, title: title?.trim() || view.defaultTitle, hint: hint ?? view.bellHint };
    const arrive = () => landing.onArrive(arrival);

    const to = visibleRect(landing.element);
    const start = originRect(from);
    const ghost = to && !prefersReducedMotion() && !pageHidden() ? createBellFlightGhost(kind) : null;
    if (!to || !ghost || typeof ghost.animate !== 'function') {
        arrive();
        return true;
    }

    document.body.append(ghost);
    const flight = ghost.animate(bellFlightFrames(start, to).map(toKeyframe), {
        duration: BELL_FLIGHT_MS,
        easing: 'linear',
        fill: 'forwards',
    });
    ghost.firstElementChild?.animate(
        [{ opacity: 0 }, { opacity: 0, offset: 0.08 }, { opacity: 1, offset: 0.3 }, { opacity: 1 }],
        {
            duration: BELL_FLIGHT_MS,
            easing: 'linear',
            fill: 'forwards',
        },
    );
    let arrived = false;
    const arriveOnce = () => {
        if (arrived) return;
        arrived = true;
        // Шапка ушла посреди полёта (выход, смена учётки): ловить некому.
        if (target === landing) arrive();
    };
    const timer = window.setTimeout(arriveOnce, BELL_FLIGHT_MS * ARRIVE_AT);
    const finish = () => {
        window.clearTimeout(timer);
        arriveOnce();
        ghost.remove();
    };
    flight.addEventListener('finish', finish, { once: true });
    flight.addEventListener('cancel', finish, { once: true });
    return true;
};
